import React, { useLayoutEffect, useRef } from 'react';
import { SI_DISPLAY_NAME } from '../siBrand';

/** Match live SpaceInvader.js playfield. */
export const SI_CANVAS_W = 1000;
export const SI_CANVAS_H = 700;
export const SI_PLAYER_SIZE = 40;
export const SI_INVADER_SIZE = 30;
export const SI_BULLET_W = 4;
export const SI_INVADER_ROWS = 5;
export const SI_INVADER_COLS = 12;
export const SI_INVADER_GAP = 15;
export const SI_INVADER_ORIGIN = 60;
export const SI_PLAYER_Y = SI_CANVAS_H - 60;
export const SI_STAR_COUNT = 150;


export const SI_PLAYFIELD_SX = {
    p: 0.5,
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.8)',
    borderRadius: 2,
    border: '2px solid rgba(0,255,0,0.3)',
    boxShadow: '0 0 20px rgba(0,255,0,0.2)',
    overflow: 'hidden',
};

export const SI_CANVAS_STYLE = {
    border: '1px solid rgba(0,255,0,0.5)',
    borderRadius: 6,
    display: 'block',
    width: '100%',
    height: 'auto',
    maxHeight: '100%',
    objectFit: 'contain',
    backgroundColor: '#000',
    aspectRatio: `${SI_CANVAS_W} / ${SI_CANVAS_H}`,
};

export function siInvaderTypeFromRow(row) {
    return row < 2 ? 'fast' : row < 4 ? 'medium' : 'slow';
}

export function defaultPlayer() {
    return {
        x: SI_CANVAS_W / 2 - SI_PLAYER_SIZE / 2,
        y: SI_PLAYER_Y,
        width: SI_PLAYER_SIZE,
        height: SI_PLAYER_SIZE,
    };
}

export function createInvaderGrid() {
    const invaders = [];
    for (let row = 0; row < SI_INVADER_ROWS; row++) {
        for (let col = 0; col < SI_INVADER_COLS; col++) {
            invaders.push({
                x: col * (SI_INVADER_SIZE + SI_INVADER_GAP) + SI_INVADER_ORIGIN,
                y: row * (SI_INVADER_SIZE + SI_INVADER_GAP) + SI_INVADER_ORIGIN,
                width: SI_INVADER_SIZE,
                height: SI_INVADER_SIZE,
                alive: true,
                type: siInvaderTypeFromRow(row),
            });
        }
    }
    return invaders;
}

/** Deterministic starfield — same scatter the live loop used, without per-frame flicker. */
export function makeStarfield(width = SI_CANVAS_W, height = SI_CANVAS_H, count = SI_STAR_COUNT) {
    const stars = [];
    for (let i = 0; i < count; i++) {
        stars.push({
            x: (i * 37) % width,
            y: (i * 47) % height,
            size: i % 5 === 0 ? 2 : 1,
        });
    }
    return stars;
}

const STARFIELD = makeStarfield();

// ---------------------------------------------------------------------------
// Original vector art (no bitmaps, no sprites from any other game).
// Swarm creatures: "darter" (fast, top rows), "orb" (medium), "carapace"
// (slow, bottom rows). Each has two animation frames and a per-level palette.
// ---------------------------------------------------------------------------

/** Per-level palettes (index = level - 1); body colours per type plus glow. */
export const SI_LEVEL_PALETTES = [
    { fast: '#3ee6c1', medium: '#7aa7ff', slow: '#b98cff', glow: '#3ee6c1', sky: '#04060f' },
    { fast: '#ffb347', medium: '#ff7a59', slow: '#e0567a', glow: '#ff9a3c', sky: '#0b0508' },
    { fast: '#c6f04d', medium: '#4dd6f0', slow: '#4d7cf0', glow: '#9ff04d', sky: '#030a08' },
    { fast: '#ff5fd2', medium: '#c86bff', slow: '#6b7bff', glow: '#ff5fd2', sky: '#09040d' },
    { fast: '#fff27a', medium: '#ffc24d', slow: '#ff6b3d', glow: '#ffd84d', sky: '#0d0703' },
];

/** Level-1 body colours (kept for callers that colour by type). */
export const SI_INVADER_COLOR = {
    fast: SI_LEVEL_PALETTES[0].fast,
    medium: SI_LEVEL_PALETTES[0].medium,
    slow: SI_LEVEL_PALETTES[0].slow,
};

export const SI_SHIP_COLOR = '#5ef0ff';
export const SI_PLAYER_SHOT_COLOR = '#9ffcff';
export const SI_ENEMY_SHOT_COLOR = '#ff4fa3';

export function siPalette(level) {
    const n = Math.max(1, Math.min(SI_LEVEL_PALETTES.length, Math.trunc(Number(level) || 1)));
    return SI_LEVEL_PALETTES[n - 1];
}

const hasPaths = (ctx) => typeof ctx.beginPath === 'function' && typeof ctx.fill === 'function';

/** Animation frame (0/1) from time, so the swarm keeps pulsing between ticks. */
export function siAnimFrame(now) {
    return Math.floor((Number(now) || 0) / 320) % 2;
}

function poly(ctx, pts) {
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath();
}

function circle(ctx, x, y, r) {
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.1, r), 0, Math.PI * 2);
}

/** Darter: arrowhead body with swept wings that beat between frames. */
function drawDarter(ctx, cx, cy, w, h, color, frame) {
    const wing = frame ? 0.18 : 0.42;
    ctx.fillStyle = color;
    poly(ctx, [[cx, cy + h * 0.45], [cx - w * 0.16, cy - h * 0.1], [cx, cy - h * 0.42], [cx + w * 0.16, cy - h * 0.1]]);
    ctx.fill();
    ctx.globalAlpha *= 0.8;
    poly(ctx, [[cx - w * 0.12, cy - h * 0.05], [cx - w * 0.5, cy - h * wing], [cx - w * 0.3, cy + h * 0.25]]);
    ctx.fill();
    poly(ctx, [[cx + w * 0.12, cy - h * 0.05], [cx + w * 0.5, cy - h * wing], [cx + w * 0.3, cy + h * 0.25]]);
    ctx.fill();
    ctx.globalAlpha /= 0.8;
    ctx.fillStyle = '#ffffff';
    circle(ctx, cx, cy - h * 0.08, w * 0.06);
    ctx.fill();
}

/** Orb: round body with an iris that looks left/right and three trailing fins. */
function drawOrb(ctx, cx, cy, w, h, color, frame) {
    const r = Math.min(w, h) * 0.32;
    ctx.fillStyle = color;
    for (let i = -1; i <= 1; i++) {
        const sway = (frame ? 1 : -1) * w * 0.06 * (i === 0 ? 0.5 : 1);
        poly(ctx, [[cx + i * r * 0.6 - r * 0.18, cy + r * 0.6], [cx + i * r * 0.6 + r * 0.18, cy + r * 0.6], [cx + i * r * 0.9 + sway, cy + h * 0.48]]);
        ctx.fill();
    }
    circle(ctx, cx, cy - h * 0.04, r);
    ctx.fill();
    ctx.fillStyle = '#0a0a14';
    circle(ctx, cx, cy - h * 0.04, r * 0.55);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    circle(ctx, cx + (frame ? r * 0.18 : -r * 0.18), cy - h * 0.04, r * 0.22);
    ctx.fill();
}

/** Carapace: armoured hexagon with a core that pulses between frames. */
function drawCarapace(ctx, cx, cy, w, h, color, frame) {
    const rx = w * 0.46;
    const ry = h * 0.36;
    ctx.fillStyle = color;
    poly(ctx, [
        [cx - rx * 0.5, cy - ry], [cx + rx * 0.5, cy - ry], [cx + rx, cy],
        [cx + rx * 0.5, cy + ry], [cx - rx * 0.5, cy + ry], [cx - rx, cy],
    ]);
    ctx.fill();
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    poly(ctx, [[cx - rx * 0.3, cy - ry * 0.55], [cx + rx * 0.3, cy - ry * 0.55], [cx + rx * 0.55, cy], [cx + rx * 0.3, cy + ry * 0.55], [cx - rx * 0.3, cy + ry * 0.55], [cx - rx * 0.55, cy]]);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    circle(ctx, cx, cy, rx * (frame ? 0.22 : 0.14));
    ctx.fill();
    ctx.fillStyle = color;
    const leg = frame ? 0.1 : -0.1;
    [-1, 1].forEach((side) => {
        poly(ctx, [[cx + side * rx * 0.7, cy + ry * 0.6], [cx + side * rx * 0.85, cy + ry * 0.6], [cx + side * rx * (1 + leg), cy + h * 0.48]]);
        ctx.fill();
    });
}

export function drawInvaderSprite(ctx, invader, opts = {}) {
    const type = invader.type || 'slow';
    const palette = opts.palette || siPalette(opts.level);
    const color = palette[type] || palette.slow;
    const w = Number(invader.width) || SI_INVADER_SIZE;
    const h = Number(invader.height) || SI_INVADER_SIZE;
    const x = Number(invader.x) || 0;
    const y = Number(invader.y) || 0;
    if (!hasPaths(ctx)) { // minimal contexts (tests): an inset silhouette block
        ctx.fillStyle = color;
        ctx.fillRect(x + w * 0.2, y + h * 0.2, w * 0.6, h * 0.6);
        return;
    }
    const frame = opts.frame != null ? opts.frame : siAnimFrame(opts.now);
    const cx = x + w / 2;
    const cy = y + h / 2;
    ctx.save();
    if ('shadowBlur' in ctx) { ctx.shadowColor = palette.glow; ctx.shadowBlur = 8; }
    if (type === 'fast') drawDarter(ctx, cx, cy, w, h, color, frame);
    else if (type === 'medium') drawOrb(ctx, cx, cy, w, h, color, frame);
    else drawCarapace(ctx, cx, cy, w, h, color, frame);
    ctx.restore();
}

/** Player ship: swept delta "interceptor" with a cockpit and a flickering engine. */
export function drawPlayerCannon(ctx, player, color = SI_SHIP_COLOR, opts = {}) {
    const x = Number(player.x) || 0;
    const y = Number(player.y) || SI_PLAYER_Y;
    const w = Number(player.width) || SI_PLAYER_SIZE;
    const h = Number(player.height) || SI_PLAYER_SIZE;
    if (!hasPaths(ctx)) {
        ctx.fillStyle = color;
        ctx.fillRect(x + w * 0.4, y + h * 0.15, w * 0.2, h * 0.7);
        ctx.fillRect(x + w * 0.1, y + h * 0.6, w * 0.8, h * 0.2);
        return;
    }
    const cx = x + w / 2;
    const flick = siAnimFrame((opts.now || 0) * 2.3);
    ctx.save();
    ctx.fillStyle = flick ? '#ffb347' : '#ff6b3d';
    poly(ctx, [[cx - w * 0.1, y + h * 0.86], [cx + w * 0.1, y + h * 0.86], [cx, y + h * (flick ? 1.08 : 0.98)]]);
    ctx.fill();
    if ('shadowBlur' in ctx) { ctx.shadowColor = color; ctx.shadowBlur = 10; }
    ctx.fillStyle = color;
    poly(ctx, [
        [cx, y + h * 0.08], [cx + w * 0.14, y + h * 0.5], [cx + w * 0.5, y + h * 0.78],
        [cx + w * 0.42, y + h * 0.9], [cx, y + h * 0.8], [cx - w * 0.42, y + h * 0.9],
        [cx - w * 0.5, y + h * 0.78], [cx - w * 0.14, y + h * 0.5],
    ]);
    ctx.fill();
    ctx.fillStyle = '#0b1830';
    poly(ctx, [[cx, y + h * 0.3], [cx + w * 0.07, y + h * 0.55], [cx - w * 0.07, y + h * 0.55]]);
    ctx.fill();
    ctx.restore();
}

/** Shield emitter (only if a caller passes bunkers): a translucent energy arc. */
export function drawBunkerSprite(ctx, bunker) {
    const w = Number(bunker.width) || 70;
    const h = Number(bunker.height) || 40;
    const x = Number(bunker.x) || 0;
    const y = Number(bunker.y) || 0;
    const health = Number(bunker.health);
    const alpha = Number.isFinite(health) ? Math.max(0.35, Math.min(1, health)) : 1;
    ctx.save();
    ctx.globalAlpha = alpha * 0.7;
    ctx.fillStyle = '#3ee6c1';
    if (hasPaths(ctx)) {
        ctx.beginPath();
        ctx.ellipse(x + w / 2, y + h, w / 2, h, 0, Math.PI, 2 * Math.PI);
        ctx.closePath();
        ctx.fill();
    } else {
        ctx.fillRect(x, y + h / 2, w, h / 2);
    }
    ctx.restore();
}

function drawBullet(ctx, b) {
    const x = Number(b.x) || 0;
    const y = Number(b.y) || 0;
    const w = Number(b.width) || SI_BULLET_W;
    const enemy = b.type === 'invader';
    const h = Number(b.height) || (enemy ? 8 : 10);
    ctx.fillStyle = enemy ? SI_ENEMY_SHOT_COLOR : SI_PLAYER_SHOT_COLOR;
    if (!hasPaths(ctx)) {
        ctx.fillRect(x, y, w, h);
        return;
    }
    ctx.save();
    if ('shadowBlur' in ctx) { ctx.shadowColor = ctx.fillStyle; ctx.shadowBlur = 8; }
    if (enemy) { // plasma blob with a short tail
        circle(ctx, x + w / 2, y + h * 0.7, w * 0.9);
        ctx.fill();
        ctx.globalAlpha = 0.5;
        circle(ctx, x + w / 2, y + h * 0.15, w * 0.5);
        ctx.fill();
    } else { // tapered energy needle
        poly(ctx, [[x + w / 2, y - 2], [x + w, y + h * 0.4], [x + w * 0.7, y + h], [x + w * 0.3, y + h], [x, y + h * 0.4]]);
        ctx.fill();
    }
    ctx.restore();
}

// --- particle explosions ----------------------------------------------------
// Invaders carry no ids, so a kill is detected when an invader of the
// previous frame has no same-type invader within KILL_MATCH_PX in the next
// frame (fleet moves <= 18 px per frame, neighbours are 45 px apart).
const KILL_MATCH_PX = 22;
const PARTICLE_LIFE_MS = 650;
const fxByCtx = typeof WeakMap === 'function' ? new WeakMap() : null;

export function findKilledInvaders(prev = [], next = []) {
    if (!prev.length || next.length >= prev.length) return [];
    const killed = [];
    for (let i = 0; i < prev.length; i++) {
        const p = prev[i];
        let hit = false;
        for (let j = 0; j < next.length && !hit; j++) {
            const n = next[j];
            if ((n.type || 'slow') !== (p.type || 'slow')) continue;
            if (Math.abs(n.x - p.x) <= KILL_MATCH_PX && Math.abs(n.y - p.y) <= KILL_MATCH_PX) hit = true;
        }
        if (!hit) killed.push(p);
    }
    return killed.length <= prev.length - next.length + 2 ? killed : killed.slice(0, prev.length - next.length);
}

export function spawnExplosion(particles, inv, color, now, seed = 0) {
    const cx = (Number(inv.x) || 0) + (Number(inv.width) || SI_INVADER_SIZE) / 2;
    const cy = (Number(inv.y) || 0) + (Number(inv.height) || SI_INVADER_SIZE) / 2;
    const n = 14;
    for (let k = 0; k < n; k++) {
        const ang = (k / n) * Math.PI * 2 + seed;
        const sp = 0.06 + ((k * 7 + seed * 13) % 5) * 0.025;
        particles.push({ x: cx, y: cy, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, t0: now, color, r: 1.5 + (k % 3) });
    }
    return particles;
}

function drawParticles(ctx, particles, now) {
    let keep = 0;
    for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const age = now - p.t0;
        if (age < 0 || age > PARTICLE_LIFE_MS) continue;
        particles[keep++] = p;
        const k = 1 - age / PARTICLE_LIFE_MS;
        ctx.save();
        ctx.globalAlpha = Math.max(0, k);
        ctx.fillStyle = p.color;
        const px = p.x + p.vx * age;
        const py = p.y + p.vy * age;
        if (hasPaths(ctx)) { circle(ctx, px, py, p.r * (0.5 + k)); ctx.fill(); } else ctx.fillRect(px, py, p.r, p.r);
        ctx.restore();
    }
    particles.length = keep;
}

const nowMs = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

/**
 * Paint one frame of the playfield.
 * Always starts with an opaque black fill so the board can never show white.
 * scene.level picks the swarm palette; scene.effects === false turns off
 * particle explosions (replays/tests); scene.now overrides the clock.
 */
export function drawSpaceInvaderScene(ctx, scene = {}) {
    const w = scene.canvasW || SI_CANVAS_W;
    const h = scene.canvasH || SI_CANVAS_H;
    const now = scene.now != null ? scene.now : nowMs();
    const palette = siPalette(scene.level);

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    if (palette.sky && typeof ctx.createLinearGradient === 'function') {
        const g = ctx.createLinearGradient(0, 0, 0, h);
        g.addColorStop(0, palette.sky);
        g.addColorStop(1, '#000');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
    }

    ctx.fillStyle = '#fff';
    const stars = scene.stars || STARFIELD;
    for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        ctx.fillRect(s.x, s.y, s.size, s.size);
    }

    const bunkers = scene.bunkers || [];
    for (let i = 0; i < bunkers.length; i++) {
        const b = bunkers[i];
        if (!b || b.alive === false) continue;
        drawBunkerSprite(ctx, b);
    }

    const invaders = (scene.invaders || []).filter((inv) => inv && inv.alive !== false);
    const frame = siAnimFrame(now);
    for (let i = 0; i < invaders.length; i++) {
        drawInvaderSprite(ctx, invaders[i], { palette, frame, now });
    }

    if (scene.effects !== false && fxByCtx && ctx && typeof ctx === 'object') {
        let fx = fxByCtx.get(ctx);
        if (!fx) { fx = { prev: [], particles: [] }; fxByCtx.set(ctx, fx); }
        const killed = findKilledInvaders(fx.prev, invaders);
        killed.forEach((inv, k) => spawnExplosion(fx.particles, inv, palette[inv.type] || palette.slow, now, k));
        fx.prev = invaders.map((inv) => ({ x: Number(inv.x) || 0, y: Number(inv.y) || 0, width: inv.width, height: inv.height, type: inv.type }));
        drawParticles(ctx, fx.particles, now);
    }

    const bullets = scene.bullets || [];
    for (let i = 0; i < bullets.length; i++) {
        if (bullets[i]) drawBullet(ctx, bullets[i]);
    }

    if (scene.player) {
        drawPlayerCannon(ctx, scene.player, SI_SHIP_COLOR, { now });
    }

    // Co-op: several ships, each { x, y, width, height, color, blink }.
    const ships = scene.ships || [];
    for (let i = 0; i < ships.length; i++) {
        const ship = ships[i];
        if (!ship || ship.blink) continue;
        drawPlayerCannon(ctx, ship, ship.color || SI_SHIP_COLOR, { now });
    }
}

function bulletTypeOf(raw, fallbackType) {
    const t = (raw && (raw.type || raw.kind || raw.owner)) || fallbackType;
    if (t === 'invader' || t === 'alien' || t === 'enemy') return 'invader';
    if (t === 'player' || t === 'cannon' || t === 'ship') return 'player';
    const dy = Number(raw?.dy ?? raw?.speed ?? raw?.vy);
    if (Number.isFinite(dy) && dy > 0) return 'invader';
    if (raw?.dir === 'down' || raw?.dir === 1) return 'invader';
    return 'player';
}

function bulletDyOf(raw, type) {
    if (raw == null) return type === 'invader' ? 4 : -8;
    if (Number.isFinite(Number(raw.dy))) return Number(raw.dy);
    if (Number.isFinite(Number(raw.speed))) return Number(raw.speed);
    if (Number.isFinite(Number(raw.vy))) return Number(raw.vy);
    if (raw.dir === 'down' || raw.dir === 1) return 4;
    if (raw.dir === 'up' || raw.dir === -1) return -8;
    return type === 'invader' ? 4 : -8;
}

/** Compact snapshot of one live bullet for history / replay (x, y, dy/dir). */
export function serializeSpaceInvaderBullet(raw, fallbackType) {
    if (raw == null || raw === false) return null;
    if (typeof raw !== 'object') return null;
    const type = bulletTypeOf(raw, fallbackType);
    const dy = bulletDyOf(raw, type);
    return {
        x: Number(raw.x) || 0,
        y: Number(raw.y) || 0,
        dy,
        dir: dy > 0 ? 'down' : 'up',
        type,
        width: Number(raw.width) || SI_BULLET_W,
        height: Number(raw.height) || (type === 'invader' ? 8 : 10),
    };
}

export function serializeSpaceInvaderBullets(list, fallbackType) {
    if (!Array.isArray(list)) return [];
    const out = [];
    for (let i = 0; i < list.length; i++) {
        const b = serializeSpaceInvaderBullet(list[i], fallbackType);
        if (b) out.push(b);
    }
    return out;
}

/** Merge player/invader arrays from a recorded tick. Missing arrays → []. */
export function collectSpaceInvaderBullets(entry) {
    if (entry == null || typeof entry !== 'object') return [];
    const out = [];
    const seen = new Set();
    const pushList = (list, fallbackType) => {
        const bullets = serializeSpaceInvaderBullets(list, fallbackType);
        for (let i = 0; i < bullets.length; i++) {
            const b = bullets[i];
            const key = `${b.type}:${Math.round(b.x)}:${Math.round(b.y)}:${Math.round(b.dy)}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(b);
        }
    };
    pushList(entry.bullets);
    pushList(entry.player_bullets, 'player');
    pushList(entry.player_shots, 'player');
    pushList(entry.invader_bullets, 'invader');
    pushList(entry.invader_shots, 'invader');
    pushList(entry.shots);
    return out;
}

export function buildSpaceInvaderTick({
    player,
    invaders = [],
    bullets = [],
    bunkers = [],
    score = 0,
    lives = 3,
    level = 1,
} = {}) {
    const all = serializeSpaceInvaderBullets(bullets);
    return {
        player_x: player?.x,
        player_y: player?.y,
        invaders: (invaders || [])
            .filter((inv) => inv && inv.alive !== false)
            .map((inv) => ({
                x: inv.x,
                y: inv.y,
                type: inv.type,
                width: inv.width,
                height: inv.height,
            })),
        bunkers: bunkers || [],
        bullets: all,
        player_bullets: all.filter((b) => b.type === 'player'),
        invader_bullets: all.filter((b) => b.type === 'invader'),
        score,
        lives,
        level,
    };
}

const SpaceInvaderScene = React.forwardRef(function SpaceInvaderScene({
    player,
    invaders,
    bunkers,
    bullets,
    level = 1,
    canvasW = SI_CANVAS_W,
    canvasH = SI_CANVAS_H,
    style,
    'data-testid': testId = 'space-invader-scene',
}, ref) {
    const innerRef = useRef(null);

    const setRef = (node) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
    };

    useLayoutEffect(() => {
        const canvas = innerRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const unmanaged = player == null && invaders == null;
        drawSpaceInvaderScene(ctx, {
            canvasW,
            canvasH,
            player: unmanaged ? defaultPlayer() : player,
            invaders: unmanaged ? createInvaderGrid() : (invaders || []),
            bunkers: bunkers || [],
            bullets: bullets || [],
            level,
        });
    }, [player, invaders, bunkers, bullets, level, canvasW, canvasH]);

    return (
        <canvas
            ref={setRef}
            data-testid={testId}
            width={canvasW}
            height={canvasH}
            role="img"
            aria-label={`${SI_DISPLAY_NAME} playfield`}
            style={{ ...SI_CANVAS_STYLE, ...style }}
        />
    );
});

export default SpaceInvaderScene;
